import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, FlaskConical, ShieldCheck, ArrowRight } from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { api } from "../lib/api";
import { nok } from "../lib/shop";
import { useLang } from "../context/LanguageContext";

// Simulert Vipps-landingsside (demo/førehandsvising). Erstattar Vipps si betalingsside når arrangøren sitt Vipps-oppsett
// har env «simulated». Kunden «godkjenner» eller «avbryt» og blir sendt tilbake til returnUrl – ingen ekte pengar flyttar seg.
export default function VippsSimulatedPage() {
  const { reference } = useParams();
  const { t } = useLang();
  const [payment, setPayment] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/vipps-simulert/${reference}`).then((res) => setPayment(res.data)).catch((err) => {
      if (err?.response?.status === 404) setNotFound(true);
      else setError(t("Kunne ikke laste betalingen"));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);

  const act = async (action) => {
    setBusy(action);
    setError("");
    try {
      const { data } = await api.post(`/vipps-simulert/${reference}/${action}`);
      setPayment(data);
      if (data.return_url) {
        setTimeout(() => window.location.assign(data.return_url), 900);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || t("Noe gikk galt"));
      setBusy("");
    }
  };

  const decided = payment && payment.state !== "CREATED";

  return (
    <PublicLayout>
      <div className="max-w-md mx-auto px-4 py-12 space-y-5" data-testid="vipps-simulated-page" data-state={payment?.state || ""}>
        <div className="flex items-center justify-between gap-3">
          <div className="inline-flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#ff5b24] text-white font-heading font-bold text-sm" aria-hidden="true">V</span>
            <span className="font-heading font-semibold">{t("Vipps-betaling")}</span>
          </div>
          <Badge variant="outline" className="border-[hsl(var(--warning))]/60 text-[hsl(var(--warning))] inline-flex items-center gap-1" data-testid="vipps-simulated-badge">
            <FlaskConical className="w-3.5 h-3.5" /> {t("Simulert")}
          </Badge>
        </div>

        <Card className="rounded-2xl border-border">
          <CardContent className="p-6 space-y-5">
            {notFound ? (
              <div className="text-center space-y-2" data-testid="vipps-simulated-not-found">
                <XCircle className="w-10 h-10 mx-auto text-destructive" />
                <p className="font-heading font-semibold">{t("Fant ikke betalingen")}</p>
                <p className="text-sm text-muted-foreground">{t("Sjekk at lenken er riktig.")}</p>
              </div>
            ) : !payment ? (
              <div className="space-y-3" data-testid="vipps-simulated-loading"><Skeleton className="h-8 w-1/2" /><Skeleton className="h-5 w-3/4" /><Skeleton className="h-11 w-full rounded-xl" /></div>
            ) : (
              <>
                <div>
                  <p className="text-sm text-muted-foreground">{t("Beløp")}</p>
                  <p className="text-3xl font-semibold tracking-tight" data-testid="vipps-simulated-amount">{nok(Math.round(payment.amount_ore / 100))}</p>
                  {payment.description && <p className="text-sm text-muted-foreground mt-1" data-testid="vipps-simulated-description">{payment.description}</p>}
                </div>

                {decided ? (
                  <div className="flex items-center gap-3" data-testid="vipps-simulated-result" data-result={payment.state}>
                    {payment.state === "AUTHORIZED" ? (
                      <><CheckCircle2 className="w-6 h-6 text-[hsl(var(--success))] shrink-0" /><div><p className="font-heading font-semibold">{t("Betalingen er godkjent")}</p><p className="text-sm text-muted-foreground">{t("Sender deg tilbake …")}</p></div></>
                    ) : (
                      <><XCircle className="w-6 h-6 text-destructive shrink-0" /><div><p className="font-heading font-semibold">{t("Betalingen ble avbrutt")}</p><p className="text-sm text-muted-foreground">{t("Sender deg tilbake …")}</p></div></>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    <Button className="w-full h-11 bg-[#ff5b24] hover:bg-[#e6501f] text-white" onClick={() => act("approve")} disabled={!!busy} data-testid="vipps-simulated-approve-button">
                      {busy === "approve" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                      {t("Godkjenn betaling")}
                    </Button>
                    <Button variant="outline" className="w-full h-11" onClick={() => act("abort")} disabled={!!busy} data-testid="vipps-simulated-abort-button">
                      {busy === "abort" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
                      {t("Avbryt")}
                    </Button>
                  </div>
                )}

                {payment.return_url && decided && (
                  <Button asChild variant="ghost" size="sm" className="w-full" data-testid="vipps-simulated-return-link">
                    <a href={payment.return_url}>{t("Gå videre nå")} <ArrowRight className="w-4 h-4 ml-1.5" /></a>
                  </Button>
                )}
              </>
            )}
            {error && <p className="text-sm text-destructive" role="alert" data-testid="vipps-simulated-error">{error}</p>}
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground" data-testid="vipps-simulated-note">
          {t("Dette er en simulert Vipps-betaling for demonstrasjon. Ingen penger trekkes. Med ekte Vipps-nøkler hos arrangøren åpnes Vipps-appen i stedet.")}
        </p>
      </div>
    </PublicLayout>
  );
}
